import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, SectionLabel, Button } from "./ui";
import { useApp } from "../context/AppContext";
import { AlertTriangle, Trash2, X } from "lucide-react";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);

export default function ResetWorkspaceCard() {
  const { lang, toast, resetWorkspace, wsRole } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // Only the workspace owner can wipe the workspace.
  if (wsRole && wsRole !== "owner") return null;

  const close = () => { if (!busy) { setOpen(false); setConfirm(""); } };

  const onReset = async () => {
    setBusy(true);
    try {
      const data = await resetWorkspace();
      toast(L(lang, `Données supprimées (${data.deleted} éléments). Nouveau départ !`,
                    `Data deleted (${data.deleted} items). Fresh start!`), { variant: "success" });
      setOpen(false);
      setConfirm("");
      navigate("/");
    } catch {
      toast(L(lang, "Échec de la réinitialisation.", "Reset failed."), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card className="mb-6 border-red-500/30" data-testid="danger-zone-card">
        <SectionLabel>{L(lang, "Zone de danger", "Danger zone")}</SectionLabel>
        <div className="flex items-start justify-between gap-4 mt-3">
          <div>
            <div className="font-heading font-semibold text-text-primary flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              {L(lang, "Réinitialiser l'espace de travail", "Reset workspace")}
            </div>
            <p className="text-sm text-text-secondary mt-1 max-w-xl">
              {L(lang, "Supprime définitivement toutes les données de votre espace (entités, tâches, projets, CRM, patrimoine, documents, conseil IA, etc.) et vous ramène au choix de départ. Votre compte et votre abonnement sont conservés.",
                       "Permanently deletes all data in your workspace (entities, tasks, projects, CRM, portfolio, documents, AI advisory, etc.) and returns you to the start choice. Your account and subscription are kept.")}
            </p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="open-reset-workspace"
            className="shrink-0 bg-red-500 hover:bg-red-600 text-white border-none">
            <Trash2 className="w-4 h-4" /> {L(lang, "Réinitialiser", "Reset")}
          </Button>
        </div>
      </Card>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          data-testid="reset-workspace-modal" onClick={close}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 rounded-xl bg-red-500/15 text-red-500 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <button onClick={close} data-testid="reset-modal-close" className="text-text-secondary hover:text-text-primary p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="font-heading font-bold text-lg text-text-primary">
              {L(lang, "Supprimer toutes les données ?", "Delete all data?")}
            </h3>
            <p className="text-sm text-text-secondary mt-2">
              {L(lang, "Cette action est irréversible. Toutes vos entités et données associées seront définitivement supprimées.",
                       "This cannot be undone. All your entities and associated data will be permanently deleted.")}
            </p>
            <label className="block text-xs text-text-secondary mt-4 mb-1">
              {L(lang, "Tapez", "Type")} <span className="font-mono font-bold text-text-primary">DELETE</span> {L(lang, "pour confirmer", "to confirm")}
            </label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus
              data-testid="reset-confirm-input" placeholder="DELETE"
              className="w-full px-3 py-2 rounded-lg bg-surface-hover border border-border text-text-primary focus:outline-none focus:ring-1 focus:ring-red-500" />
            <div className="flex gap-3 mt-5">
              <Button onClick={close} disabled={busy}
                className="flex-1 justify-center bg-surface-hover hover:bg-border text-text-primary border-none">
                {L(lang, "Annuler", "Cancel")}
              </Button>
              <Button onClick={onReset} disabled={busy || confirm.trim() !== "DELETE"}
                data-testid="confirm-reset-workspace"
                className="flex-1 justify-center bg-red-500 hover:bg-red-600 text-white border-none disabled:opacity-50">
                {busy ? L(lang, "Suppression…", "Deleting…") : L(lang, "Tout supprimer", "Delete everything")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
